import React, { Component } from 'react';
import { array, bool, func, object, shape, string, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { createResourceLocatorString } from '../../util/routes';
import { getCategoryUrl, getCategoryBreadcrumbPath } from '../../util/categoryLandingHelpers';
import {
  isMainSearchTypeKeywords,
  getQueryParamNames,
  isOriginInUse,
} from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { getListingCategories } from '../../ducks/hostedAssets.duck';

import { BottomNav, H5, Notice, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
} from './SearchPage.shared';

import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import CategoryFacetSidebar from './CategoryFacetSidebar/CategoryFacetSidebar';
import FilterBar from './FilterBar/FilterBar';
import FilterPanel from './FilterPanel/FilterPanel';
import FloatingFilterBar from './FloatingFilterBar/FloatingFilterBar';
import { splitHeadAndOverflowFilters, narrowFilterOptions, isFilterActive } from './FilterBar/filterBarHelpers';

import css from './SearchPage.module.css';
import { withViewport } from '../../util/uiHelpers';

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isFilterPanelOpen: false,
    };

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
  }

  // Reset local filter state when URL changes externally (e.g. X chip, back/forward)
  // so sidebar filters reinitialize from the URL and don't re-submit stale values.
  componentDidUpdate(prevProps) {
    if (prevProps.location.search !== this.props.location.search) {
      this.setState({ currentQueryParams: {} });
    }
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params — also clear showSoldOut since it's not in filterQueryParamNames
    // (it's a special UI-only param, not a config-driven filter)
    const queryParams = omit(urlQueryParams, [...filterQueryParamNames, 'showSoldOut']);
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config, listingCategories } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      // If a category level filter changed, navigate to the category landing page
      // carrying over any active non-category filters
      if (useHistoryPush) {
        const categoryKeys = ['pub_categoryLevel1', 'pub_categoryLevel2', 'pub_categoryLevel3', 'pub_categoryLevel4'];
        const hasCategoryChange = categoryKeys.some(k => k in updatedURLParams);
        if (hasCategoryChange) {
          // Use the deepest level that has a value set
          const changedCategoryKey = [...categoryKeys].reverse().find(k => updatedURLParams[k]);
          const categoryId = changedCategoryKey ? updatedURLParams[changedCategoryKey] : null;
          const categoryPath = categoryId ? getCategoryUrl(categoryId) : null;
          if (categoryPath) {
            // Strip category keys from current URL params
            const nonCategoryParams = Object.keys(urlQueryParams).reduce((acc, k) => {
              if (!categoryKeys.includes(k)) acc[k] = urlQueryParams[k];
              return acc;
            }, {});
            // Build target category level params so omitLimitedListingFieldParams
            // knows which filters are still compatible with the target category
            const targetBreadcrumb = getCategoryBreadcrumbPath(categoryId);
            const targetCategoryLevelParams = targetBreadcrumb.reduce((acc, cat, index) => {
              acc[`pub_categoryLevel${index + 1}`] = cat.id;
              return acc;
            }, {});
            const filterConfigs = { listingFieldsConfig, defaultFiltersConfig, listingCategories };
            const filtered = omitLimitedListingFieldParams(
              { ...nonCategoryParams, ...targetCategoryLevelParams },
              filterConfigs
            );
            // Category is encoded in the URL path — strip level keys from query string
            categoryKeys.forEach(k => delete filtered[k]);
            const search = Object.keys(filtered).length
              ? `?${new URLSearchParams(filtered).toString()}`
              : '';
            history.push(`${categoryPath}${search}`);
            return;
          }
        }
      }

      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = useHistoryPush
          ? { ...urlQueryParams }
          : { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};

        // Clean params to remove filters that are not valid for current category selection
        const filterConfigs = {
          listingFieldsConfig,
          defaultFiltersConfig,
          listingCategories: listingCategories,
        };

        const cleanedParams = omitLimitedListingFieldParams(
          {
            ...mergedQueryParams,
            ...updatedURLParams,
            ...keywordsMaybe,
            address,
            bounds,
          },
          filterConfigs
        );

        return {
          currentQueryParams: cleanedParams,
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(
            searchParams,
            listingFieldsConfig,
            defaultFiltersConfig,
            sortConfig
          );
          history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      routeConfiguration,
      config,
      history,
      viewport,
      listingCategories,
    } = this.props;

    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;
    const isMobileLayout = viewport && viewport.width > 0 && viewport.width < 768;
    // Prototype breakpoints: the pill row runs from 550 up; only true phone
    // widths get the floating Filters/Sort pill
    const isPhoneLayout = viewport && viewport.width > 0 && viewport.width < 550;
    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      listingFieldsConfig,
      defaultFiltersConfig,
      sortConfig,
      isOriginInUse(config)
    );

    const validQueryParams = validFilterParams(
      searchParamsInURL,
      listingFieldsConfig,
      defaultFiltersConfig,
      false
    );

    const priceFilters = defaultFiltersConfig.filter(f => f.key === 'price');
    const otherDefaultFilters = defaultFiltersConfig.filter(
      f => f.schemaType !== 'category' && !['price', 'keywords', 'dates'].includes(f.key)
    );

    // Use category-limited filtering to only show relevant filters
    const categoryFilteredListingFields = pickListingFieldFilters({
      listingFields: listingFieldsConfig,
      locationSearch: location.search,
      categoryConfiguration: {
        key: 'categoryLevel', // Default key for hosted categories
        categories: listingCategories,
      },
    });

    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      categoryFilteredListingFields,
      activeListingTypes
    );

    // Handler for the "Show Only > Sold Out Items" toggle.
    // Pushes showSoldOut=true to the URL when enabled, removes it when disabled.
    // showSoldOut is a special UI-only param — see SearchPage.duck.js stockFilters()
    // for how it is consumed before the Sharetribe API call.
    const handleToggleSoldOut = checked => {
      const updatedParams = checked
        ? { ...urlQueryParams, showSoldOut: 'true' }
        : omit(urlQueryParams, 'showSoldOut');
      history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, updatedParams));
    };

    // Hybrid filter row + panel (ledger §2). On keyword search the head shows
    // Condition + up to 3 fields PRESENT in the current result set (file
    // order) + Price — without a category context, raw file order would
    // surface obscure category fields. Panel gets everything, presence-
    // narrowed.
    const facets = this.props.facets;
    const presence = facets?.presence || null;
    const keywordCustomFilters = [...customPrimaryFilters, ...customSecondaryFilters];
    const presentCustomFilters = presence
      ? keywordCustomFilters.filter(f => f.key === 'condition' || presence[f.key])
      : keywordCustomFilters.filter(f => ['condition', 'global_made_in'].includes(f.key));
    const panelDefaultFilters = [...priceFilters, ...otherDefaultFilters];
    const { head: headFilters, overflow: overflowFilters } = splitHeadAndOverflowFilters(
      presentCustomFilters,
      panelDefaultFilters
    );
    const narrowedHeadFilters = headFilters.map(f =>
      narrowFilterOptions(f, presence, urlQueryParams)
    );
    const panelFilters = [...keywordCustomFilters, ...panelDefaultFilters];


    // Selected aka active filters
    const selectedFilters = validFilterParams(
      validQueryParams,
      listingFieldsConfig,
      defaultFiltersConfig
    );
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>

    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer
          className={css.topbar}
          currentPage="SearchPage"
          currentSearchParams={urlQueryParams}
        />
        <main className={css.layoutWrapperContainer}>
          {/* Prototype keyword results: H1 "Results for “term”" + count line
              above the sidebar/grid columns */}
          {!isMobileLayout && validQueryParams.keywords ? (
            <div className={css.resultsHeadingBlock}>
              <h1 className={css.resultsHeading}>
                <FormattedMessage
                  id="SearchPage.resultsForHeading"
                  values={{ keywords: validQueryParams.keywords }}
                />
              </h1>
              {listingsAreLoaded ? (
                <div className={css.resultsHeadingCount}>
                  <FormattedMessage id="SearchPage.resultsForCount" values={{ count: totalItems }} />
                </div>
              ) : null}
            </div>
          ) : (
            // Every page has one h1: here it's for screen readers only (the design shows none)
            <h1 className="visuallyHidden">
              {validQueryParams.keywords ? (
                <FormattedMessage
                  id="SearchPage.resultsForHeading"
                  values={{ keywords: validQueryParams.keywords }}
                />
              ) : (
                <FormattedMessage id="SearchPage.browseHeading" />
              )}
            </h1>
          )}
          <div className={css.searchMainContent}>
            <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
              {/* Keyword results: the sidebar is a category FACET of the
                  result set (README screen 4); filters live in the row +
                  slide-out panel. */}
              <CategoryFacetSidebar facets={facets} keywords={validQueryParams.keywords} />
            </aside>
            <div className={css.layoutWrapperMain} role="main">
              <div className={css.searchResultContainer}>
                {/* Phone: floating Filter/Sort bar + bottom-sheet panel */}
                {isPhoneLayout ? (
                  <>
                    <FloatingFilterBar
                      onOpenPanel={() => this.setState({ isFilterPanelOpen: true })}
                      activeCount={[...headFilters, ...overflowFilters].filter(f =>
                        isFilterActive(f, urlQueryParams)
                      ).length}
                      sortConfig={sortConfig}
                      urlQueryParams={urlQueryParams}
                      hasKeywords={!!validQueryParams.keywords}
                      onSortBy={this.handleSortBy}
                      isPanelOpen={this.state.isFilterPanelOpen}
                    />
                    {noResultsInfo}
                  </>
                ) : null}
                {!isPhoneLayout ? (
                  <>
                    {/* Prototype: the pill row is absent on zero results —
                        the zero-results card carries the escape hatches */}
                    {listingsAreLoaded && totalItems === 0 ? null : (
                    <FilterBar
                      headFilters={narrowedHeadFilters}
                      overflowFilters={overflowFilters}
                      urlQueryParams={urlQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      marketplaceCurrency={marketplaceCurrency}
                      listingCategories={listingCategories}
                      intl={intl}
                      totalItems={totalItems}
                      listingsAreLoaded={listingsAreLoaded}
                      sortConfig={sortConfig}
                      onSortBy={this.handleSortBy}
                      hasKeywords={!!validQueryParams.keywords}
                      onOpenPanel={() => this.setState({ isFilterPanelOpen: true })}
                      onOpenPanelField={fieldKey =>
                        this.setState({ isFilterPanelOpen: true, panelFocusKey: fieldKey })
                      }
                    />
                    )}
                    {noResultsInfo}
                  </>
                ) : null}
                <FilterPanel
                  isOpen={this.state.isFilterPanelOpen}
                  onClose={() => this.setState({ isFilterPanelOpen: false, panelFocusKey: null })}
                  focusFieldKey={this.state.panelFocusKey}
                  filters={panelFilters}
                  presence={presence}
                  urlQueryParams={urlQueryParams}
                  initialValues={initialValues(this.props, this.state.currentQueryParams)}
                  getHandleChangedValueFn={this.getHandleChangedValueFn}
                  marketplaceCurrency={marketplaceCurrency}
                  listingCategories={listingCategories}
                  intl={intl}
                  totalItems={totalItems}
                  onToggleSoldOut={handleToggleSoldOut}
                  onClearAll={e => this.handleResetAll(e)}
                  onManageDisableScrolling={onManageDisableScrolling}
                />
                <div
                  className={classNames(css.listingsForGridVariant, {
                    [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                  })}
                >
                  {searchListingsError ? (
                    <Notice tone="error">
                      <p>
                        <FormattedMessage id="SearchPage.searchError" />
                      </p>
                    </Notice>
                  ) : null}
                  {!isValidDatesFilter ? (
                    <H5>
                      <FormattedMessage id="SearchPage.invalidDatesFilter" />
                    </H5>
                  ) : null}
                  {/* No results: no empty results panel (the empty state above says so) */}
                  {listings?.length > 0 ? (
                    <SearchResultsPanel
                      className={css.searchListingsPanel}
                      listings={listings}
                      pagination={listingsAreLoaded ? pagination : null}
                      search={parse(location.search)}
                      isMapVariant={false}
                      isSearchPage
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </main>
        <BottomNav />
        <FooterContainer />
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
};

SearchPageComponent.propTypes = {
  listings: array,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    facets,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);
  const { currentUser } = state.user;

  return {
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    facets,
    listingCategories: getListingCategories(state),
    currentUser,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport
)(EnhancedSearchPage);

export default SearchPage;
